// ═══════════════════════════════════════════════════════════════
// VendorProfilePanel.jsx — verticalstack XC-NET vendor detail view
// Path: src/components/VendorProfilePanel.jsx
//
// S8.C (June 29, 2026) — NEW FILE. Vendor profile detail surfaced from
// the Network roster (Network.jsx "View" action). Distinct filename from
// the legacy orphan VendorProfile.jsx (old S13 epoch, imported only by the
// dead PMApp.js) per AS-S8-VENDORPROFILE-LEGACY — no collision, orphan
// left in place for the post-S37 cleanup pass.
// S8.D (June 29, 2026) — EDIT. Surface the computed Trustack score.
// The Trustack card now calls computeTrustack(vendor) (trustack.js v1):
//   - computed score 0–100 + advisory tier-eligibility + Inactive flag
//   - per-signal sub-score breakdown when live signals exist
//   - graceful "—" dormant state when no signals (every live vendor today)
// The four stored attribute tiles (rating/jobs/response/firstFix) remain
// as factual vendor attributes below the score.
//
// WHAT IT IS
//   In-view detail panel for a single vendor from the platform-wide
//   /vendors roster. Network.jsx owns selectedVendor state and renders
//   this in place of the roster table; onBack clears it back to the list.
//
// PROPS
//   vendor — the selected vendor doc (canonical §6 shape)
//   onBack — () => void, returns to the Network roster table
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { computeTrustack } from './trustack';

const NAVY = '#003060';
const CYAN = '#00B8D0';

const TIER_META = {
  T1: { label: 'T1', descriptor: 'verified',  color: '#0A5C42', bg: '#E8F7F2' },
  T2: { label: 'T2', descriptor: 'adjacent',  color: '#7A4A08', bg: '#FDF3E3' },
  T3: { label: 'T3', descriptor: 'open pool', color: '#5A6478', bg: '#F0F2F5' },
};

const SUBSCORE_LABELS = {
  external: 'External',
  responsiveness: 'Response',
  completion: 'Completion',
  recency: 'Recency',
};

function prettyTrades(trades) {
  if (!trades || trades.length === 0) return [];
  return trades.map((t) =>
    String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function metricOrDash(v, suffix = '') {
  if (v === null || v === undefined || v === '') return '—';
  return `${v}${suffix}`;
}

export default function VendorProfilePanel({ vendor, onBack }) {
  if (!vendor) return null;
  const tm = TIER_META[vendor.tier] || TIER_META.T3;
  const trades = prettyTrades(vendor.trades);
  const trust = computeTrustack(vendor);

  return (
    <div style={styles.container}>
      {/* ── Back ──────────────────────────────────────────────────── */}
      <button type="button" style={styles.backBtn} onClick={onBack}>
        ← Back to Network
      </button>

      {/* ── Header ────────────────────────────────────────────────── */}
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div>
            <h1 style={styles.name}>{vendor.name || 'Unnamed vendor'}</h1>
            <div style={styles.subline}>
              {vendor.area && <span>{vendor.area}</span>}
              {vendor.area && <span style={styles.dot}>·</span>}
              <span style={styles.idText}>{vendor.id}</span>
            </div>
          </div>
          <div style={styles.headerBadges}>
            <span style={{ ...styles.tierBadge, color: tm.color, background: tm.bg }}>
              {tm.label} · {tm.descriptor}
            </span>
            <span style={styles.onboardPill}>{vendor.onboardingState || 'active'}</span>
          </div>
        </div>
      </div>

      <div style={styles.grid2}>
        {/* ── Contact ─────────────────────────────────────────────── */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>Contact</div>
          <div style={styles.kvRow}>
            <span style={styles.k}>Name</span>
            <span style={styles.v}>{vendor.contact?.name || '—'}</span>
          </div>
          <div style={styles.kvRow}>
            <span style={styles.k}>Phone</span>
            <span style={styles.vMono}>{vendor.contact?.phone || '—'}</span>
          </div>
          <div style={styles.kvRow}>
            <span style={styles.k}>Service area</span>
            <span style={styles.v}>{vendor.area || '—'}</span>
          </div>
        </div>

        {/* ── Compliance ──────────────────────────────────────────── */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>Compliance</div>
          <div style={styles.kvRow}>
            <span style={styles.k}>Insurance (COI)</span>
            {vendor.insurance
              ? <span style={styles.okBadge}>✓ on file</span>
              : <span style={styles.gapBadge}>not on file</span>}
          </div>
          <div style={styles.kvRow}>
            <span style={styles.k}>W-9</span>
            {vendor.w9OnFile
              ? <span style={styles.okBadge}>✓ on file</span>
              : <span style={styles.gapBadge}>not on file</span>}
          </div>
          <div style={styles.kvRow}>
            <span style={styles.k}>Payment terms</span>
            <span style={styles.v}>{vendor.paymentTerms || '—'}</span>
          </div>
          <div style={styles.note}>
            W-9 and COI are ingested via AI-08 OCR when documents are uploaded.
          </div>
        </div>
      </div>

      {/* ── Trustack score (computed — trustack.js v1) ────────────── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Trustack score</div>

        {trust.hasLiveSignals ? (
          <>
            <div style={styles.scoreRow}>
              <div style={styles.scoreBlock}>
                <span style={styles.scoreValue}>{trust.score}</span>
                <span style={styles.scoreMax}>/ 100</span>
              </div>
              <div style={styles.scoreBadges}>
                {trust.tierEligible && (
                  <span style={styles.eligibleBadge}>Eligible: {trust.tierEligible}</span>
                )}
                {trust.inactive && (
                  <span style={styles.inactiveBadge}>Inactive · 90-day decay</span>
                )}
              </div>
            </div>

            <div style={styles.subScoreGrid}>
              {Object.keys(SUBSCORE_LABELS).map((k) => (
                <div key={k} style={styles.subScore}>
                  <div style={styles.subScoreVal}>
                    {trust.subScores[k] != null ? trust.subScores[k] : '—'}
                  </div>
                  <div style={styles.subScoreLabel}>
                    {SUBSCORE_LABELS[k]}
                    <span style={styles.subScoreWeight}> ·{trust.weights[k]}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={styles.scoreDormant}>
            <span style={styles.scoreValueDormant}>—</span>
            <span style={styles.scoreDormantNote}>
              No live signals yet. Score computes once external reviews,
              Twilio responsiveness, and completed work orders begin
              feeding in (S9+ / S13).
            </span>
          </div>
        )}

        <div style={styles.note}>
          Weighted blend — external {trust.weights.external} / responsiveness{' '}
          {trust.weights.responsiveness} / completion {trust.weights.completion}{' '}
          / recency {trust.weights.recency}. Algorithm v1 (client-side);
          moves to a scheduled Cloud Function at S13.
        </div>

        {/* ── Stored attributes ─────────────────────────────────── */}
        <div style={styles.attrHead}>Vendor attributes</div>
        <div style={styles.metricGrid}>
          <div style={styles.metric}>
            <div style={styles.metricVal}>{vendor.rating != null ? Number(vendor.rating).toFixed(1) : '—'}</div>
            <div style={styles.metricLabel}>Rating</div>
          </div>
          <div style={styles.metric}>
            <div style={styles.metricVal}>{metricOrDash(vendor.jobs)}</div>
            <div style={styles.metricLabel}>Jobs</div>
          </div>
          <div style={styles.metric}>
            <div style={styles.metricVal}>{vendor.response != null ? `${vendor.response}h` : '—'}</div>
            <div style={styles.metricLabel}>Avg response</div>
          </div>
          <div style={styles.metric}>
            <div style={styles.metricVal}>{vendor.firstFix != null ? `${vendor.firstFix}%` : '—'}</div>
            <div style={styles.metricLabel}>First-fix</div>
          </div>
        </div>
      </div>

      {/* ── Payments ──────────────────────────────────────────────── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Payments</div>
        <div style={styles.kvRow}>
          <span style={styles.k}>Stripe Connect</span>
          {vendor.stripe
            ? <span style={styles.okBadge}>✓ linked</span>
            : <span style={styles.gapBadge}>not linked</span>}
        </div>
        {vendor.stripe && (
          <div style={styles.kvRow}>
            <span style={styles.k}>Account</span>
            <span style={styles.vMono}>{vendor.stripe}</span>
          </div>
        )}
        <div style={styles.note}>
          Stripe Connect enrollment wires with vendor onboarding (S9.D).
        </div>
      </div>

      {/* ── Trades ────────────────────────────────────────────────── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Trades</div>
        {trades.length === 0 ? (
          <div style={styles.emptyInline}>No trades listed.</div>
        ) : (
          <div style={styles.chipRow}>
            {trades.map((t) => (
              <span key={t} style={styles.chip}>{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* ── Prior-job history (shaped, empty — AS-S8-JOBHISTORY-EMPTY-SHAPED) ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Prior-job history</div>
        <div style={styles.historyHead}>
          <span style={styles.historyCount}>{metricOrDash(vendor.jobs)}</span>
          <span style={styles.historyCountLabel}>total jobs across all workspaces</span>
        </div>
        <div style={styles.emptyState}>
          {(vendor.jobs || 0) === 0
            ? 'No completed jobs yet.'
            : `${vendor.jobs} job(s) recorded.`}{' '}
          The job list populates as Marketplace (S9) creates and completes work
          orders against this vendor — full history wires at S13 when the
          workOrders collection ships.
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 1100, margin: '0 auto', fontFamily: 'Arial, sans-serif', color: '#1F2937' },
  backBtn: {
    background: 'transparent', border: 'none', color: CYAN, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', padding: '4px 0', marginBottom: 12, fontFamily: 'inherit',
  },
  headerCard: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10,
    padding: 20, marginBottom: 16,
  },
  headerTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    flexWrap: 'wrap', gap: 12,
  },
  name: { fontSize: 24, fontWeight: 700, color: NAVY, margin: 0 },
  subline: { fontSize: 13, color: '#6B7280', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  dot: { color: '#D0D5DD' },
  idText: { fontFamily: 'Consolas, monospace', fontSize: 12, color: '#9CA3AF' },
  headerBadges: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  tierBadge: { fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 12, fontFamily: 'Consolas, monospace' },
  onboardPill: {
    fontSize: 11, fontWeight: 700, color: NAVY, background: '#F0F4F8',
    padding: '4px 12px', borderRadius: 12, textTransform: 'capitalize',
  },
  grid2: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 16, marginBottom: 16,
  },
  card: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: 18, marginBottom: 16,
  },
  cardHeader: {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#9CA3AF',
    textTransform: 'uppercase', marginBottom: 14,
  },
  kvRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 0', borderBottom: '1px solid #F1F5F9', gap: 12,
  },
  k: { fontSize: 12, color: '#6B7280', fontWeight: 600 },
  v: { fontSize: 13, color: '#374151', textAlign: 'right' },
  vMono: { fontSize: 12, color: '#374151', fontFamily: 'Consolas, monospace', textAlign: 'right' },
  okBadge: { fontSize: 11, fontWeight: 700, color: '#0A5C42', background: '#E8F7F2', padding: '3px 10px', borderRadius: 10 },
  gapBadge: { fontSize: 11, fontWeight: 700, color: '#9CA3AF', background: '#F3F4F6', padding: '3px 10px', borderRadius: 10 },
  note: { marginTop: 12, fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', lineHeight: 1.5 },

  // ── Trustack score block (S8.D) ───────────────────────────────────────
  scoreRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 12, marginBottom: 14,
  },
  scoreBlock: { display: 'flex', alignItems: 'baseline', gap: 6 },
  scoreValue: { fontSize: 40, fontWeight: 700, color: NAVY, lineHeight: 1 },
  scoreMax: { fontSize: 15, color: '#9CA3AF', fontWeight: 600 },
  scoreBadges: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  eligibleBadge: {
    fontSize: 11, fontWeight: 700, color: '#0A5C42', background: '#E8F7F2',
    padding: '4px 12px', borderRadius: 12,
  },
  inactiveBadge: {
    fontSize: 11, fontWeight: 700, color: '#7A4A08', background: '#FDF3E3',
    padding: '4px 12px', borderRadius: 12,
  },
  subScoreGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 4 },
  subScore: { textAlign: 'center', padding: '8px 4px', background: '#F0F7FA', borderRadius: 8 },
  subScoreVal: { fontSize: 18, fontWeight: 700, color: CYAN },
  subScoreLabel: { fontSize: 10, color: '#6B7280', marginTop: 2 },
  subScoreWeight: { color: '#B0B7C3' },
  scoreDormant: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 6, flexWrap: 'wrap' },
  scoreValueDormant: { fontSize: 40, fontWeight: 700, color: '#D0D5DD', lineHeight: 1 },
  scoreDormantNote: { fontSize: 12, color: '#6B7280', lineHeight: 1.5, flex: 1, minWidth: 200 },

  attrHead: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: '#9CA3AF',
    textTransform: 'uppercase', margin: '18px 0 10px',
  },
  metricGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  metric: { textAlign: 'center', padding: '8px 4px', background: '#F8FAFC', borderRadius: 8 },
  metricVal: { fontSize: 20, fontWeight: 700, color: NAVY },
  metricLabel: { fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 },

  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    fontSize: 11, color: '#374151', background: '#F0F4F8', border: '1px solid #E5E8EE',
    padding: '5px 12px', borderRadius: 12, fontFamily: 'Consolas, monospace',
  },
  historyHead: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 },
  historyCount: { fontSize: 28, fontWeight: 700, color: NAVY },
  historyCountLabel: { fontSize: 12, color: '#6B7280' },
  emptyState: { color: '#6B7280', fontSize: 13, fontStyle: 'italic', lineHeight: 1.55 },
  emptyInline: { color: '#9CA3AF', fontSize: 13, fontStyle: 'italic' },
};
